import { VENDOR_REGISTRY } from '../../data/sessions';
import { C, Badge, Card, Row, vendorColor } from './shared';

export default function ArchTab() {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Card title="Live URLs" accent={C.green}>
        <Row label="Frontend" value="marketpulze.com" accent={C.green} />
        <Row label="Backend (prod)" value="api.marketpulze.com (OCI VM-1 · Always Free)" accent={C.green} />
        <Row label="Backend (dev)" value="dev.api.marketpulze.com (OCI Dev VM · Always Free)" accent={C.info} />
        <Row label="GitHub" value="github.com/Marketpulze/marketpulze" accent={C.muted} />
        <Row label="Deploy" value="dev -> merge -> main (never direct to main)" accent={C.warning} />
        <Row label="Monthly cost" value="~$30/mo (Railway CANCELLED · OCI Always Free · MongoDB $0-9 + domain $1 + AI API ~$20)" accent={C.green} />
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="Frontend" accent={C.info}>
          <Row label="Framework" value="React CRA" />
          <Row label="Routing" value="Custom pushState (NO React Router)" />
          <Row label="Styling" value="ALL inline styles (no CSS/Tailwind)" />
          <Row label="Hosting" value="Cloudflare Pages (free CDN)" />
          <Row label="CI env var" value="CI=true (CRITICAL — Cloudflare treats warnings as errors)" accent={C.danger} />
        </Card>
        <Card title="Backend" accent={C.purple}>
          <Row label="Runtime" value="Node.js 18 + Express" />
          <Row label="Structure" value="Modular routes (no monolith in server.js)" />
          <Row label="Hosting" value="OCI VM-1 A1.Flex (Always Free · api.marketpulze.com)" />
          <Row label="Database" value="MongoDB Atlas (marketpulze-prod)" />
          <Row label="Auth" value="JWT 24hr sessions" />
          <Row label="Email" value="SMTP2GO via fetch() - no npm package" />
        </Card>
      </div>
      <Card title="Data Providers" accent={C.warning}>
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 8 }}>
          Source: sessions.js VENDOR_REGISTRY — single source of truth
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {(VENDOR_REGISTRY || []).map(p => {
            const col = vendorColor(p.status);
            return (
              <div key={p.name} style={{ background: C.surface, borderRadius: 8, padding: 12, border: '1px solid ' + col + '33' }}>
                <div style={{ color: col, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{p.name}</div>
                <Badge color={col}>{p.status}</Badge>
                <div style={{ color: C.muted, fontSize: 11, marginTop: 6 }}>{p.note}</div>
              </div>
            );
          })}
        </div>
      </Card>
      <Card title="Caching Architecture" accent={C.info}>
        {[
          ['Stock quotes', 'node-cache', '15 min', 'Per ticker - in memory'],
          ['Chart / News / Earnings / Consensus', 'node-cache depthCache', '15 min', 'stocks-seo.js'],
          ['Market overview + movers + sectors', 'node-cache', '15 min', 'services/market.js'],
          ['Fundamentals (P/E, EPS, 52W, Beta)', 'MongoDB FundamentalsCache', '24 hours', 'Fixes weekend blanks'],
          ['Historical price series (calculator)', 'MongoDB HistoricalCache', '7 days', 'Saves AV 500/day limit'],
          ['Screener tickers (20 seed)', 'MongoDB ScreenerCache + NodeCache', '24hr/15min', '3-tier: NodeCache→MongoDB→AV. Stale fallback never shows empty.'],
          ['Drawing layouts', 'MongoDB Drawing', 'Permanent', 'Member chart drawings — restored on /chart/:ticker load (S27G)'],
          ['Indicator layouts', 'MongoDB IndicatorLayout (S27H)', 'Permanent', 'Member indicator presets — restored on full chart page visit'],
          ['Stooq OHLCV', 'OCI NoSQL (stooq_daily · stooq_intraday · stooq_macro_daily)', '30yr/180d/5min', 'R29: internal D3 only — never serve to members. Initial code written · STOOQ-PIPELINE session pending.'],
        ].map(([d, t, ttl, n]) => (
          <div key={d} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 80px 2fr',
            gap: 8, padding: '8px 0', borderBottom: '1px solid ' + C.border + '22', alignItems: 'center' }}>
            <span style={{ color: C.text, fontSize: 12 }}>{d}</span>
            <span style={{ color: C.info, fontSize: 12 }}>{t}</span>
            <Badge color={C.green}>{ttl}</Badge>
            <span style={{ color: C.muted, fontSize: 11 }}>{n}</span>
          </div>
        ))}
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="Backend Route Files" accent={C.purple}>
          {[
            ['routes/auth.js', 'Login, register, verify, reset, invite'],
            ['routes/stocks.js', 'Member AI analysis (auth required)'],
            ['routes/stocks-seo.js', 'Public SEO + depth (rate-limited)'],
            ['routes/watchlist.js', 'Watchlist + digest + unsubscribe'],
            ['routes/market.js', '/overview /movers /sectors'],
            ['routes/calculator.js', '/validate + /calculate (DRIP)'],
            ['routes/chart-signals.js', 'D3 Tier 3 — chart interaction logging (S27E+)'],
            ['routes/drawings.js', 'Chart drawing persistence GET/POST/DELETE (S27G)'],
            ['routes/indicator-layouts.js', 'Indicator preset persistence (S27H ✅ DONE)'],
            ['routes/admin.js', 'All RBAC admin endpoints'],
          ].map(([f, d]) => (
            <div key={f} style={{ padding: '7px 0', borderBottom: '1px solid ' + C.border + '22' }}>
              <div style={{ color: C.info, fontSize: 12, fontFamily: 'monospace' }}>{f}</div>
              <div style={{ color: C.muted, fontSize: 11 }}>{d}</div>
            </div>
          ))}
        </Card>
        <Card title="Frontend Pages + Routes" accent={C.warning}>
          {[
            ['/', 'Home - hero + mini indices + watchlist panel'],
            ['/market', 'Market Dashboard - indices, movers, heatmap'],
            ['/stocks/:ticker', 'Stock research page (229 tickers, SEO)'],
            ['/chart/:ticker', 'Full-page advanced chart — S27H planned'],
            ['/screener', 'Stock Screener — 7 D3-mapped scans, guest top-10 lock'],
            ['/dashboard', 'Member command center + D3StatusPanel'],
            ['/calculator', 'Historical performance (guest access)'],
            ['/share/:uuid', 'Shareable card page (Session 23)'],
            ['/profile', 'Member profile, history, delete account'],
            ['/admin', 'Admin panel - 4 tabs (admin role)'],
            ['/roadmap', 'This page (Super Admin only)'],
          ].map(([r, d]) => (
            <div key={r} style={{ padding: '7px 0', borderBottom: '1px solid ' + C.border + '22' }}>
              <div style={{ color: r.includes('S27H') ? C.muted : C.warning, fontSize: 12, fontFamily: 'monospace' }}>{r}</div>
              <div style={{ color: C.muted, fontSize: 11 }}>{d}</div>
            </div>
          ))}
        </Card>
      </div>
      <Card title="MongoDB Collections" accent={C.info}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {[
            { n: 'User', f: 'email, password, status, emailVerified, digestOptIn, digestOptInAt, unsubscribeToken, analysesThisMonth, subscriptionTier' },
            { n: 'FundamentalsCache', f: 'ticker, peRatio, eps, week52High/Low, beta, _source, expiresAt (24hr TTL)' },
            { n: 'HistoricalCache', f: 'cacheKey, data (monthly series + dividends), expiresAt (7-day TTL)' },
            { n: 'Watchlist', f: 'userId, tickers[] max 10, updatedAt' },
            { n: 'InviteRequest', f: 'email, name, reason, status, reviewedBy, reviewedAt' },
            { n: 'Settings', f: 'invite_emails[], beta_open, admins[] (key/value store)' },
            { n: 'AuditLog', f: 'action, performedBy, target, details (auto-purge at 200)' },
            { n: 'ShareCard (S23)', f: 'uuid, ticker, signal, score, price, createdBy, expiresAt (30-day TTL)' },
            { n: 'ChartInteractionLog', f: 'ticker, eventType, timeframe, priceAtCrosshair, memberId, marketStatus, drawingType (S27E+)' },
            { n: 'Drawing', f: 'memberId, ticker, drawingType, price/price1/price2, logical1/logical2, color (S27G)' },
            { n: 'DrawingLog', f: 'D3 Tier 3 fire-and-forget — crowd price level clustering signal (S27G)' },
            { n: 'IndicatorLayout (S27H)', f: 'memberId, ticker, indicators[], templateName — restored on /chart/:ticker visit' },
          ].map(m => (
            <div key={m.n} style={{ background: C.surface, borderRadius: 8, padding: 12 }}>
              <div style={{ color: C.info, fontWeight: 700, fontSize: 12, marginBottom: 6 }}>{m.n}</div>
              <div style={{ color: C.muted, fontSize: 10, lineHeight: 1.5 }}>{m.f}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="D3 Tier 3 Signal Sources — The Moat" accent={C.purple}>
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 12 }}>
          Every interaction = proprietary signal. No vendor sells member behaviour data at any price.
        </div>
        {[
          { source: 'ScreenerSignalLog', session: 'S27D ✅', signal: 'Filter patterns, scan clicks, scan sequences → D3 strategy assignment', color: C.green },
          { source: 'ChartInteractionLog', session: 'S27E ✅', signal: 'Timeframe switches, hover price levels, ticker views, SMA crossovers', color: C.green },
          { source: 'DrawingLog', session: 'S27G ✅', signal: 'Member-drawn S/R levels → crowd-validated price levels — strongest signal', color: C.green },
          { source: 'IndicatorLayout (S27H)', session: 'PLANNED', signal: 'Indicator combos + templates → strategy fingerprint per member', color: C.warning },
          { source: 'FibonacciLog (S27I)', session: 'PLANNED', signal: 'Fibonacci levels at same price from multiple members = institutional S/R', color: C.warning },
          { source: 'ScanContextLog (S27I)', session: 'PLANNED', signal: 'Scan→chart→draw chain = highest-confidence strategy confirmation signal', color: C.warning },
          { source: 'WatchlistLog', session: 'S21 ✅', signal: 'Watchlist adds/removes per ticker', color: C.green },
          { source: 'CalculatorUsage', session: 'S22C ✅', signal: 'DCA patterns, tickers calculated, date ranges', color: C.green },
        ].map(s => (
          <div key={s.source} style={{ display: 'grid', gridTemplateColumns: '200px 90px 1fr',
            gap: 10, padding: '8px 0', borderBottom: '1px solid ' + C.border + '22', alignItems: 'start' }}>
            <span style={{ color: s.color, fontWeight: 700, fontSize: 12, fontFamily: 'monospace' }}>{s.source}</span>
            <Badge color={s.color}>{s.session}</Badge>
            <span style={{ color: C.muted, fontSize: 11 }}>{s.signal}</span>
          </div>
        ))}
      </Card>

      <Card title="D3 Strategy Universe" accent={C.purple}>
        {[
          { id: 'A', name: 'Momentum Day Trading', signal: 'Trend + volume + RSI + VWAP. Same-day exit. 1-2% risk/trade.', hold: 'Same day', size: '1-2%', stop: 'VWAP break' },
          { id: 'B', name: 'News Speculation', signal: 'Breaking news + earnings + FDA + M&A. Hours to days. 0.5-1% risk.', hold: 'Hours-days', size: '0.5-1%', stop: 'News reversal' },
          { id: 'C', name: 'Pump & Dump Detection', signal: 'Volume anomaly + social spike + float < 50M + no legit news.', hold: 'Minutes-hours', size: '0.5% MAX', stop: 'Volume drops' },
          { id: 'E', name: 'Value + Insider', signal: 'PE below sector + revenue growth + insider buying + 52W low.', hold: 'Weeks-months', size: '3-5%', stop: 'Thesis broken' },
          { id: 'F', name: 'Short Squeeze (3 modes)', signal: 'High short float >10% + institutional accumulation + good fundamentals.', hold: 'Days-months', size: '2-4%', stop: 'Mode-specific' },
          { id: 'G', name: 'Insider Cluster', signal: '3+ distinct insiders buying within 30 days. CEO+CFO = CLUSTER_ELITE.', hold: 'Weeks-months', size: '3-5%', stop: 'Cluster breaks' },
          { id: 'H', name: 'Consolidation Breakout', signal: 'Gap → consolidation 10-35d → volume dry-up → breakout >1.5x vol.', hold: '3-14 days', size: '2-3%', stop: 'Below consolidation' },
          { id: 'I', name: 'Fraud Investigation Short', signal: 'Late 10-K/10-Q + prominent short seller + SEC 8-K keyword alert.', hold: 'Days-weeks', size: '1-2%', stop: 'Price target hit' },
        ].map(s => (
          <div key={s.id} style={{
            display: 'grid', gridTemplateColumns: '28px 1fr 80px 60px 110px',
            gap: 8, padding: '7px 0', borderBottom: '1px solid ' + C.border + '22',
            alignItems: 'center',
          }}>
            <span style={{ background: C.purple + '33', color: C.purple, fontWeight: 800,
              fontSize: 12, width: 22, height: 22, display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRadius: 4 }}>{s.id}</span>
            <div>
              <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{s.name}</div>
              <div style={{ color: C.muted, fontSize: 10 }}>{s.signal}</div>
            </div>
            <span style={{ color: C.muted, fontSize: 11 }}>{s.hold}</span>
            <span style={{ color: C.green, fontSize: 11, fontWeight: 700 }}>{s.size}</span>
            <span style={{ color: C.warning, fontSize: 11 }}>{s.stop}</span>
          </div>
        ))}
        <div style={{ marginTop: 14, padding: '10px 12px', background: C.purple + '11',
          borderRadius: 8, border: '1px solid ' + C.purple + '33' }}>
          <div style={{ color: C.purple, fontWeight: 700, fontSize: 11, marginBottom: 4 }}>
            Multi-scenario convergence rules
          </div>
          <div style={{ color: C.muted, fontSize: 11 }}>
            F Mode 2 + G CLUSTER_STRONG + H breakout = ULTRA_HIGH_CONFIDENCE (5% cap, owner approval)<br/>
            G CLUSTER_BIOTECH_ELITE + FDA PDUFA within 30 days = highest biotech conviction<br/>
            F Mode 1 + G CLUSTER_STRONG = maximum long conviction (institutions + insiders aligned)
          </div>
        </div>
      </Card>

      <Card title="OCI Data Pipeline — Free Data Sources" accent={C.info}>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
          One-time build on OCI ATP. Permanent free data. Replaces $2,800+/yr of vendor fees.
        </div>
        {[
          { source: 'Stooq Daily OHLCV', target: 'OCI NoSQL stooq_daily', size: '506MB', cost: 'Free', note: 'R29: internal D3 only — never display raw. 30yr history for D3 backtesting. STOOQ-PIPELINE pending.' },
          { source: 'Stooq 5-min Intraday', target: 'OCI NoSQL stooq_intraday', size: '~3GB rolling', cost: 'Free', note: 'R29: internal D3 only. STOOQ-PIPELINE pending.' },
          { source: 'EDGAR Form 4', target: 'Oracle SQL edgar_events · edgar_insider', size: '~2GB', cost: 'Free', note: 'Public domain. edgarDisplayPipeline (RSS+safety net) + edgarSignalPipeline (REFACTOR-EDGAR). Initial code written.' },
          { source: 'EDGAR XBRL', target: 'Oracle SQL (OCI-5E session)', size: '~8GB', cost: 'Free', note: 'Public domain. OCI-5E session planned. Not yet built.' },
          { source: 'Congressional PTR', target: 'Oracle SQL congressional_trades', size: '~200MB', cost: 'Free', note: 'STOCK Act. senatePipeline + housePipeline (search endpoint primary). Initial code written · PIPELINE-RELOAD pending. Rotation signals detected in-memory during load (arch gap — see PIPELINE_STATUS.md).' },
          { source: 'Alpha Vantage Plan30', target: 'Live quote display', size: '75 req/min', cost: '$499/yr', note: 'Display-licensed. Upgrade trigger: first 10 active members.' },
          { source: 'Congressional (senate+house)', target: 'Oracle SQL congressional_trades', size: '~4K+ rows', cost: 'Free', note: 'senatePipeline + housePipeline · 11pm ET chain · PIPELINE-RELOAD pending validation' },
          { source: 'Sector mapping (SEC)', target: 'Oracle SQL sector_mapping', size: '~20K rows', cost: 'Free', note: 'sectorMappingPipeline · 2am ET daily · dual-source company_tickers' },
          { source: 'Lobbying (LDA)', target: 'Oracle SQL lobbying_disclosures', size: 'quarterly', cost: 'Free', note: 'lobbyingPipeline · Sunday chain · LDA_API_TOKEN vault · PIPELINE-RELOAD pending' },
          { source: 'FEC campaign finance', target: 'Oracle SQL fec_member_funding', size: 'per cycle', cost: 'Free', note: 'fecPipeline · 1st Sunday monthly · FEC_API_KEY vault · PIPELINE-RELOAD pending' },
          { source: 'Federal Register', target: 'Oracle SQL federal_register_orders', size: 'daily', cost: 'Free', note: 'federalRegisterPipeline · 6am ET Mon-Fri · no auth required' },
        ].map(p => (
          <div key={p.source} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 80px 60px',
            gap: 8, padding: '8px 0', borderBottom: '1px solid ' + C.border + '22', alignItems: 'start' }}>
            <div>
              <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{p.source}</div>
              <div style={{ color: C.muted, fontSize: 10 }}>{p.note}</div>
            </div>
            <span style={{ color: C.info, fontSize: 11, fontFamily: 'monospace' }}>{p.target}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>{p.size}</span>
            <Badge color={p.cost === 'Free' ? C.green : C.warning}>{p.cost}</Badge>
          </div>
        ))}
      </Card>

      <Card title="D3 Multi-Layer LLM Engine" accent={C.purple}>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
          7 layers. Triple consensus (L3+L4+L5) required before IB Gateway executes. Layer divergence = training data for L6.
        </div>
        {[
          { l: 'L1', model: 'Llama 4 Scout', conf: 'HIGH', role: 'Signal scoring every 5min', cost: '~$2/mo', trigger: 'Always', color: C.green },
          { l: 'L2', model: 'Gemini 2.5 Flash', conf: 'HIGH', role: 'Pattern classification', cost: '~$4/mo', trigger: 'L1 threshold breach', color: C.green },
          { l: 'L3', model: 'Llama 4 Maverick', conf: 'REASONABLE', role: 'Deep strategy reasoning', cost: '~$8/mo', trigger: 'L2 confidence > 0.75', color: C.warning },
          { l: 'L4', model: 'gpt-oss-120b', conf: 'UNKNOWN→EVALUATE', role: 'Cross-validation (OCI-3 eval)', cost: '~$5/mo', trigger: 'L3 PROCEED', color: C.danger },
          { l: 'L5', model: 'Claude Sonnet', conf: 'FULL', role: 'Capital gate — independent of OCI', cost: '~$2/mo', trigger: 'L3+L4 both PROCEED', color: C.green },
          { l: 'L6', model: 'Llama 4 Maverick', conf: 'REASONABLE', role: 'Weekly self-improvement', cost: '<$1/mo', trigger: 'Sunday 2am ET', color: C.warning },
          { l: 'L7', model: 'Claude Sonnet', conf: 'FULL', role: 'User intelligence — AI Brief', cost: '~$2/mo', trigger: 'User-facing only', color: C.green },
        ].map(l => (
          <div key={l.l} style={{ display: 'grid', gridTemplateColumns: '28px 1.5fr 100px 2fr 80px',
            gap: 8, padding: '6px 0', borderBottom: '1px solid ' + C.border + '22', alignItems: 'center' }}>
            <span style={{ background: C.purple + '33', color: C.purple, fontWeight: 800,
              fontSize: 11, width: 22, height: 22, display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRadius: 4 }}>{l.l}</span>
            <div>
              <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{l.model}</div>
              <div style={{ color: C.muted, fontSize: 10 }}>{l.role}</div>
            </div>
            <Badge color={l.color}>{l.conf}</Badge>
            <span style={{ color: C.muted, fontSize: 11 }}>{l.trigger}</span>
            <span style={{ color: C.green, fontSize: 11, fontWeight: 700 }}>{l.cost}</span>
          </div>
        ))}
        <div style={{ marginTop: 12, padding: '8px 12px', background: C.danger + '11', borderRadius: 6, border: '1px solid ' + C.danger + '33' }}>
          <span style={{ color: C.danger, fontWeight: 700, fontSize: 11 }}>L4 gpt-oss-120b: UNKNOWN confidence — post-knowledge-cutoff model. Must evaluate in OCI-3 session before any D3 role. Do not assign capital-risk role until evaluated.</span>
        </div>
      </Card>

      <Card title="D3 Infrastructure — Status + Fallbacks" accent={C.danger}>
        {[
          { label: 'OCI PAYG Contract',    status: 'ACTIVE ✅', note: 'Contract 1224179463.a1 · DSQUARE LLC · US East Ashburn. VM-1 (api.marketpulze.com) + VM-PIPELINE + Dev VM all running. VM-2 (OCI-8 · D3+IB Gateway) still to provision.', color: C.green },
          { label: 'Hetzner CX22 fallback', status: 'STANDBY', note: 'If OCI blocked 60+ days → EUR3.79/mo, 2 vCPU 4GB. IB Gateway capable. Activate immediately.', color: C.warning },
          { label: 'IB Gateway (port 4001)', status: 'PLANNED', note: 'Java process 24/7. Needs OCI/Hetzner persistent server. Railway CANNOT run this. (Railway decommissioned May 4, 2026 — OCI VM-1 is now primary.)', color: C.warning },
          { label: 'Redis (OCI)',            status: 'PLANNED', note: 'D3 order state + PDT counter. AOF persistence required. Restart without AOF = lose state.', color: C.info },
          { label: 'IBKR Level 2',          status: 'PLANNED', note: 'NYSE OpenBook ~$3 + NASDAQ TotalView ~$3 = ~$6/mo. MANDATORY before D3b real capital.', color: C.warning },
          { label: 'Emergency stop',        status: 'PLANNED', note: '/trading/emergency PWA page. Phone home screen shortcut. Mandatory before D3c.', color: C.danger },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid ' + C.border + '22' }}>
            <div style={{ flex: 1 }}>
              <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{item.label}</span>
              <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{item.note}</div>
            </div>
            <Badge color={item.color}>{item.status}</Badge>
          </div>
        ))}
      </Card>
    </div>
  );
}

